import { Localization } from "@viamap/viamap2-common";
import { PropertyInfoInterface } from "./PropertyInfoInterface";

type Idents = {
   bfe: string;
   bfe_type: "SFE" | "BPFG" | "Ejerlejlighed";
   lat: number;
   lng: number;
   x: number;
   y: number;
}

export function createRawExtender<T extends object>(object: T & {
   bfe_nr: string;
   bfe_ejendomsType: "SamletFastEjendom" | "BygningPaaFremmedGrund" | "Ejerlejlighed";
   koord_lat: number;
   koord_lng: number;
   koord_x: number;
   koord_y: number;
}): { raw: T, ident: Idents } {

   const indentTypeMap = {
      "SamletFastEjendom": "SFE",
      "BygningPaaFremmedGrund": "BPFG",
      "Ejerlejlighed": "Ejerlejlighed"
   } as const;

   return {
      raw: object, ident: {
         bfe: object.bfe_nr,
         bfe_type: indentTypeMap[object.bfe_ejendomsType] || object.bfe_ejendomsType,
         lat: object.koord_lat,
         lng: object.koord_lng,
         x: object.koord_x,
         y: object.koord_y
      }
   };
}

export function ExtendWithDHM<T extends object & { ident: Idents }>(object: T): T & { dhm: { højde: number } | null } {
   const dhmValue = Math.random() * 100;
   return { ...object, dhm: { højde: dhmValue ?? -1 } };
}

export function ExtendsWithZone<T extends object & { ident: Idents }>(object: T): T & { zone: { status: string } | null } {
   if ("zone_status" in object["raw"] && typeof object["raw"]["zone_status"] === "string") {
      const zoneValue = object["raw"]["zone_status"];
      return { ...object, zone: { status: zoneValue ?? "" } };
   }
   return { ...object, zone: null };
}

export function ExtendsWithOIEKorrigering<T extends object & { ident: Idents }>(objectArr: T[]): (T & {
   oie: {
      bolig_korrigeret: number | null,
      erhverv_korrigeret: number | null,
      samlet_korrigeret: number | null,
   } | null
})[] {
   return objectArr.map((object) => {
      if ("bbr_hovedejendomOpdeltIEjerlejligheder" in object["raw"] && object["raw"]["bbr_hovedejendomOpdeltIEjerlejligheder"]) {
         let underDele = objectArr.filter((oB) => (oB["raw"]["bfe_sfe_nr"] == object["raw"]["bfe_sfe_nr"]) && (oB.ident.bfe != object.ident.bfe));
         return {
            ...object,
            oie: {
               bolig_korrigeret: (object["bbr"]["bolig"] ?? 0) - underDele.reduce((pre, b) => (pre + (b["bbr"]["bolig"] ?? 0)), 0),
               erhverv_korrigeret: (object["bbr"]["erhverv"] ?? 0) - underDele.reduce((pre, b) => (pre + (b["bbr"]["erhverv"] ?? 0)), 0),
               samlet_korrigeret: (object["bbr"]["samlet"] ?? 0) - underDele.reduce((pre, b) => (pre + (b["bbr"]["samlet"] ?? 0)), 0),
            }
         }
      }
      return { ...object, oie: null };
   });
}

export async function ExtendWithBBRData<T extends object>(object: T & { ident: Idents }): Promise<T & { bbr: { [key: string]: any } | null }> {
   if (object.ident.bfe_type === "Ejerlejlighed") {
      let BBREnhedder = await PropertyInfoInterface.BBREnhedForBFE("" + object.ident.bfe) as any[];
      let aktuelle = BBREnhedder.filter((enhed) => ["6", "7", "8", "9", "12"].includes(enhed.status))

      return {
         ...object, bbr: {
            samlet: aktuelle.reduce((pre, a) => (pre + (a.enh026EnhedensSamledeAreal || 0)), 0) ?? 0,
            bolig: aktuelle.reduce((pre, a) => (pre + (a.enh027ArealTilBeboelse || 0)), 0) ?? 0,
            erhverv: aktuelle.reduce((pre, a) => (pre + (a.enh028ArealTilErhverv || 0)), 0) ?? 0
         }
      }
      // TODO Extends og change function to be ExtendEjerlejlighedWithBBRData
   }
   return {
      ...object, bbr: {
         bolig: object["raw"]["bbr_ejd_bygningensSamledeBoligAreal"] ?? 0,
         erhverv: object["raw"]["bbr_ejd_samletErhvervsAreal"] ?? 0,
         samlet: object["raw"]["bbr_ejd_samletBygningsAreal"] ?? 0,
      }
   }
}

export function CreateMainSheet<T extends object & { ident: Idents }>(object: T): T & { mainSheet: { [key: string]: string } } {
   let sheetKeys = [
      { key: "raw.bfe_sfe_nr", da: "SFE nr", en: "SFE nr", beskrivelse: "SFE nummeret for ejendommen" },
      { key: "ident.bfe", da: "BFE nr", en: "BFE nr", beskrivelse: "BFE nummeret for ejendommen" },
      {
         key: "ident.bfe_type", da: "Ejendomstype", en: "Property type", beskrivelse: "Ejendomstypen for ejendommen", parser: (value: string) => {
            const map = {
               "SFE": "Samlet Fast Ejendom",
               "BPFG": "Bygning på fremmed grund",
               "Ejerlejlighed": "Ejerlejlighed"
            };
            return map[value as keyof typeof map] || value;
         }
      },
      { key: "ident.lat", da: "Latitude", en: "Latitude", beskrivelse: "Latitude koordinat for ejendommen" },
      { key: "ident.lng", da: "Longitude", en: "Longitude", beskrivelse: "Longitude koordinat for ejendommen" },
      { key: "dhm.højde", da: "DHM værdi", en: "DHM value", beskrivelse: "DHM værdi for ejendommen" },
      { key: "zone.status", da: "Zone", en: "Zone", beskrivelse: "Zone for ejendommen" },
      { key: "raw.bbr_hovedejendomOpdeltIEjerlejligheder", da: "Opdelt i EJL", en: "Divided in EJL", beskrivelse: "Om ejendommen er opdelt i ejerlejligheder", parser: (value: string) => value ? "Ja" : "Nej" },
      { key: "oie.bolig_korrigeret", da: "Bolig korrigeret", en: "Residential corrected", beskrivelse: "Korrigeret bolig areal for ejendommen" },
      { key: "oie.erhverv_korrigeret", da: "Erhverv korrigeret", en: "Commercial corrected", beskrivelse: "Korrigeret erhverv areal for ejendommen" },
      { key: "oie.samlet_korrigeret", da: "Samlet korrigeret", en: "Total corrected", beskrivelse: "Korrigeret samlet areal for ejendommen" },
      { key: "bbr.samlet", da: "BBR samlet", en: "BBR total", beskrivelse: "Samlet BBR areal for ejendommen" },
      { key: "bbr.bolig", da: "BBR bolig", en: "BBR residential", beskrivelse: "Bolig BBR areal for ejendommen" },
      { key: "bbr.erhverv", da: "BBR erhverv", en: "BBR commercial", beskrivelse: "Erhverv BBR areal for ejendommen" },
   ]

   let sheet = sheetKeys.map(sk =>
      retrieveSheetKeys(object, sk)
   );
   return { ...object, mainSheet: Object.fromEntries(sheet) };
}

function retrieveSheetKeys(
   object: any,
   sheetKey: { key: string, da: string, en: string, beskrivelse: string, parser?: (value: string) => string | undefined }
): [string, string] {
   let returnKey = Localization.getLanguage() === "da" ? sheetKey.da : sheetKey.en;
   if (!sheetKey.key.includes('.')) {
      const value = object[sheetKey.key];
      return [returnKey, sheetKey?.parser ? sheetKey.parser(value ?? "") ?? "" : value ?? ""];
   }
   const parts = sheetKey.key.split('.');
   let val = object;
   while (parts.length > 0) {
      const part = parts.shift()!;
      if (val && part in val) {
         val = val[part];
      } else {
         return [returnKey, ""];
      }
   }
   return [returnKey, sheetKey?.parser ? sheetKey.parser(val ?? "") ?? "" : val ?? ""];
}